/**
 * useIsAdmin — reads the server-derived admin flag from the session.
 *
 * A9 fix (QA 2026-06-11): the previous implementation probed authorisation by
 * calling admin.ping once per mount — an RPC that 403s BY DESIGN for every
 * non-admin session. The browser logs every 4xx network response to the
 * console regardless of application-level handling, so every app load shipped
 * an unsuppressable `admin/ping 403` console error for every normal user.
 *
 * The server now injects `user.isAdmin` into the GET /api/auth/get-session
 * payload (apps/api/src/index.ts, same block as the `handle` injection; the
 * comparison is `isAdminEmail` — the exact non-throwing twin of assertAdmin).
 * This hook reads it:
 *   1. From the localStorage session cache (written by app.tsx on every
 *      session fetch) — zero network when the entry carries the flag.
 *   2. Falling back to one GET /api/auth/get-session when the cache misses
 *      or the entry predates the isAdmin field. That request is a 200 for
 *      every session state — no console noise, no 403 by design.
 *
 * Any error (network failure, unexpected payload) silently defaults to false —
 * this is passive detection, not a user action, so quiet failure is correct
 * per CLAUDE.md Rule 8 (which governs mutations, not probes).
 *
 * Architecture refs:
 *   apps/api/src/middleware/assert-admin.ts — isAdminEmail (server twin)
 *   apps/web/src/lib/session-cache.ts — cache shape + TTL
 *   .plan/features/2026-05-01-ui-coverage-recovery.md §4 PR7 (original hook)
 */

import { useState, useEffect } from "react";
import { readSessionCache } from "../../lib/session-cache.js";

/** Read the isAdmin flag from the session cache: true/false when the entry
 *  carries it, null when unknown (cache miss, unauthenticated, or an entry
 *  written before the field existed). */
function readCachedIsAdmin(): boolean | null {
  const cached = readSessionCache();
  if (cached?.session.kind !== "authenticated") return null;
  return typeof cached.session.isAdmin === "boolean" ? cached.session.isAdmin : null;
}

/**
 * Returns true when the current session is an admin session, false otherwise.
 * Resolves synchronously from the session cache when possible; otherwise
 * stabilises after one quiet get-session round-trip (renders false until then).
 */
export function useIsAdmin(): boolean {
  const [isAdmin, setIsAdmin] = useState<boolean>(() => readCachedIsAdmin() === true);

  useEffect(() => {
    const cached = readCachedIsAdmin();
    if (cached !== null) {
      setIsAdmin(cached);
      return;
    }

    // Cache miss or pre-isAdmin entry — one get-session fetch. 200 for every
    // session state (admin, non-admin, signed out): no 403-by-design noise.
    let cancelled = false;
    fetch("/api/auth/get-session", {
      credentials: "include",
      headers: { Accept: "application/json" },
    })
      .then((res) =>
        res.status === 200
          ? (res.json() as Promise<{ user?: { isAdmin?: boolean } | null } | null>)
          : null,
      )
      .then((data) => {
        if (!cancelled && data?.user?.isAdmin === true) setIsAdmin(true);
      })
      // eslint-disable-next-line no-restricted-syntax -- best-effort: an admin-affordance probe; not-signed-in / not-configured / offline all correctly leave isAdmin false (Rule 8 documented degradation).
      .catch(() => {
        // Not signed in, not configured, or network failure — stay false.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return isAdmin;
}
